import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { Area, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { T_MAX, areaPath, curvePath, loadAt, niceStep, r1 } from "./vtModel";
import { BG, FAINT, LINE_STRONG, MUTED, TEAL, TEXT } from "./vtTheme";

interface SparklineProps {
  max: number;
  rate: number;
  /** Load at the top of the box, shared across rows so curves compare. */
  yMax: number;
  stroke: string;
  fill: string;
  width?: number;
  height?: number;
}

/** A small accumulation curve for a table row or a library entry. */
export function Sparkline({ max, rate, yMax, stroke, fill, width = 88, height = 28 }: SparklineProps) {
  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" style={{ width, height, display: "block", overflow: "visible", flex: "none" }} aria-hidden="true">
      <path d={areaPath(max, rate, yMax, 100, 30)} fill={fill} />
      <path d={curvePath(max, rate, yMax, 100, 30)} fill="none" stroke={stroke} strokeWidth={1.6} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export interface CurveParams {
  max: number;
  rate: number;
}

interface AccumulationChartProps {
  /** The selected type's curve, or null while its values are invalid. */
  current: CurveParams | null;
  /** The saved curve, drawn dashed while the values are edited. */
  saved: CurveParams | null;
  /** Every other type, drawn faint for comparison. */
  others: (CurveParams & { key: string })[];
  unit: string;
  /** Loads above this are off the chart. */
  cap: number;
}

const POINTS = 50;

/** Fuel load over the years since fire for the selected type. */
export function AccumulationChart({ current, saved, others, unit, cap }: AccumulationChartProps) {
  const { data, yMax, yTicks } = useMemo(() => {
    let top = 0;
    [current, saved].forEach((c) => { if (c && c.max <= cap && c.max > top) top = c.max; });
    if (!(top > 0)) top = 5;
    const step = niceStep((top * 1.15) / 4);
    const yMax = Math.ceil((top * 1.15) / step - 1e-9) * step;
    const yTicks: number[] = [];
    for (let v = 0; v <= yMax + 1e-9; v += step) yTicks.push(Math.round(v * 100) / 100);
    const data = Array.from({ length: POINTS + 1 }, (_, i) => {
      const t = (i * T_MAX) / POINTS;
      const row: Record<string, number> = { t };
      if (current) row.cur = loadAt(current.max, current.rate, t);
      if (saved) row.saved = loadAt(saved.max, saved.rate, t);
      others.forEach((o) => { row[o.key] = loadAt(o.max, o.rate, t); });
      return row;
    });
    return { data, yMax, yTicks };
  }, [current, saved, others, cap]);

  return (
    <Box>
      <Box sx={{ height: 200, ml: -1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 10, right: 6, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.07)" />
            <XAxis dataKey="t" type="number" domain={[0, T_MAX]} ticks={[0, 5, 10, 15, 20, 25]} tick={{ fill: FAINT, fontSize: 10 }} tickLine={false} stroke={LINE_STRONG} />
            <YAxis domain={[0, yMax]} ticks={yTicks} allowDataOverflow width={34} tick={{ fill: FAINT, fontSize: 10 }} tickLine={false} axisLine={false} />
            {others.map((o) => (
              <Line key={o.key} dataKey={o.key} type="monotone" stroke="rgba(255,255,255,0.2)" strokeWidth={1.1} dot={false} activeDot={false} isAnimationActive={false} />
            ))}
            {current && <ReferenceLine y={current.max} stroke="rgba(0,212,173,0.45)" strokeDasharray="2 4" ifOverflow="hidden" />}
            {saved && <Line dataKey="saved" type="monotone" stroke="rgba(255,255,255,0.75)" strokeWidth={1.6} strokeDasharray="5 4" dot={false} activeDot={false} isAnimationActive={false} />}
            {current && <Area dataKey="cur" type="monotone" stroke={TEAL} strokeWidth={2.4} fill="rgba(0,212,173,0.1)" dot={false} isAnimationActive={false} />}
            <Tooltip
              cursor={{ stroke: LINE_STRONG }}
              content={({ active, label, payload }) => {
                if (!active || !payload?.length) return null;
                const row = payload[0].payload as Record<string, number>;
                return (
                  <Box sx={{ bgcolor: BG, border: `1px solid ${LINE_STRONG}`, borderRadius: 1.5, px: 1.25, py: 0.75, fontSize: 12, color: TEXT }}>
                    <Box sx={{ color: MUTED }}>{r1(Number(label))} yrs since fire</Box>
                    {row.cur !== undefined && <Box>{r1(row.cur)} {unit}</Box>}
                    {row.saved !== undefined && <Box sx={{ color: MUTED }}>Saved {r1(row.saved)} {unit}</Box>}
                  </Box>
                );
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", pl: "26px" }}>
        <Typography sx={{ fontSize: 10, color: FAINT }}>{unit}</Typography>
        <Typography sx={{ fontSize: 10, color: FAINT }}>Years since fire</Typography>
      </Box>
    </Box>
  );
}
